import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Breadcrumb, Button, Descriptions, Empty, Form, Input, Modal, Pagination, Select, Space, Table, Upload, message } from 'antd';
import { DownloadOutlined, EditOutlined, HomeOutlined, LockOutlined, SearchOutlined, UnlockOutlined, UploadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../api';
import { ReactComponent as BedIcon } from '../assets/bed.svg';
import './DormitoryManagementPage.css';

const normalizeBedNumber = value => {
  const match = String(value ?? '').match(/\d+/);
  return match ? Number(match[0]) : null;
};

const getDormitoryBedSlots = dormitory => {
  const residents = Array.isArray(dormitory.residents) ? dormitory.residents : [];
  const residentsByBed = new Map();

  residents.forEach(resident => {
    const bedNumber = normalizeBedNumber(resident.bedNumber);
    if (bedNumber && !residentsByBed.has(bedNumber)) {
      residentsByBed.set(bedNumber, resident);
    }
  });

  const configuredCapacity = Number(dormitory.bedCapacity) || 0;
  const highestAssignedBed = Math.max(0, ...residentsByBed.keys());
  const bedCapacity = configuredCapacity > 0 ? configuredCapacity : highestAssignedBed;

  return Array.from({ length: bedCapacity }, (_, index) => {
    const bedNumber = index + 1;
    return { bedNumber, resident: residentsByBed.get(bedNumber) };
  });
};

const DormitoryManagementPage = () => {
  const [dormitories, setDormitories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [buildingFilter, setBuildingFilter] = useState();
  const [genderFilter, setGenderFilter] = useState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [selectedDormitory, setSelectedDormitory] = useState(null);
  const [editingGenderType, setEditingGenderType] = useState();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedDormitoryIds, setSelectedDormitoryIds] = useState([]);
  const [batchGenderType, setBatchGenderType] = useState();
  const [editingStudent, setEditingStudent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [locking, setLocking] = useState(false);
  const [form] = Form.useForm();

  const fetchDormitories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accommodation/dormitories');
      setDormitories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDormitories([]);
      message.error('获取寝室数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDormitories();
  }, []);

  const filteredDormitories = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    return dormitories.filter(dormitory => [
      dormitory.building,
      dormitory.dormitoryNumber,
      ...dormitory.residents.map(student => student.name),
    ].some(value => String(value || '').toLowerCase().includes(keyword))
      && (!buildingFilter || dormitory.building === buildingFilter)
      && (!genderFilter || dormitory.genderType === genderFilter))
      .sort((left, right) => {
        const buildingOrder = String(left.building || '').localeCompare(String(right.building || ''), 'zh-CN', { numeric: true });
        return buildingOrder || String(left.dormitoryNumber || '').localeCompare(
          String(right.dormitoryNumber || ''),
          'zh-CN',
          { numeric: true }
        );
      });
  }, [dormitories, searchText, buildingFilter, genderFilter]);

  const pagedDormitories = useMemo(() => filteredDormitories.slice(
    (page - 1) * pageSize,
    page * pageSize
  ), [filteredDormitories, page, pageSize]);

  const buildings = useMemo(() => [...new Set(dormitories.map(dormitory => dormitory.building).filter(Boolean))], [dormitories]);
  const dormitoryNumbers = useMemo(() => [...new Set(dormitories.map(dormitory => dormitory.dormitoryNumber).filter(Boolean))], [dormitories]);
  const bedNumbers = useMemo(() => [...new Set(
    dormitories.flatMap(dormitory => dormitory.residents.map(student => student.bedNumber)).filter(Boolean)
  )], [dormitories]);

  useEffect(() => {
    setPage(1);
  }, [searchText, buildingFilter, genderFilter, pageSize]);

  const openEdit = student => {
    setEditingStudent(student);
    form.setFieldsValue({
      building: student.building,
      dormitoryNumber: student.dormitoryNumber,
      bedNumber: student.bedNumber,
    });
    setEditOpen(true);
  };

  const saveAccommodation = async () => {
    try {
      const values = await form.validateFields();
      await api.put(`/accommodation/students/${editingStudent.id}`, values);
      message.success('住宿信息已更新');
      setEditOpen(false);
      form.resetFields();
      setSelectedDormitory(null);
      await fetchDormitories();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || '更新住宿信息失败');
    }
  };

  const saveDormitoryGender = async () => {
    if (!selectedDormitory || !editingGenderType) return;
    try {
      await api.put(`/accommodation/dormitories/${selectedDormitory.id}/gender`, { genderType: editingGenderType });
      message.success('寝室类型已更新');
      setSelectedDormitory({ ...selectedDormitory, genderType: editingGenderType });
      await fetchDormitories();
    } catch (error) {
      message.error(error.response?.data?.message || '更新寝室类型失败');
    }
  };

  const openLockModal = () => {
    setLockReason(selectedDormitory?.lockReason || '');
    setLockOpen(true);
  };

  const lockDormitory = async () => {
    const reason = lockReason.trim();
    if (!reason) {
      message.warning('请填写锁定原因');
      return;
    }
    setLocking(true);
    try {
      await api.put('/accommodation/dormitories/' + selectedDormitory.id + '/lock', { reason });
      message.success('寝室已锁定');
      setSelectedDormitory({ ...selectedDormitory, locked: true, lockReason: reason });
      setLockOpen(false);
      await fetchDormitories();
    } catch (error) {
      message.error(error.response?.data?.message || '锁定寝室失败');
    } finally {
      setLocking(false);
    }
  };

  const unlockDormitory = () => {
    if (!selectedDormitory) return;
    Modal.confirm({
      title: '解锁寝室',
      content: '确认解除 ' + selectedDormitory.building + '-' + selectedDormitory.dormitoryNumber + ' 的锁定吗？',
      okText: '解锁',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.put('/accommodation/dormitories/' + selectedDormitory.id + '/unlock');
          message.success('寝室已解锁');
          setSelectedDormitory({ ...selectedDormitory, locked: false, lockReason: null });
          await fetchDormitories();
        } catch (error) {
          message.error(error.response?.data?.message || '解锁寝室失败');
        }
      },
    });
  };

  const deleteDormitory = () => {
    if (!selectedDormitory) return;
    Modal.confirm({
      title: '删除寝室',
      content: `确认删除 ${selectedDormitory.building}-${selectedDormitory.dormitoryNumber} 吗？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/accommodation/dormitories/${selectedDormitory.id}`);
          message.success('寝室已删除');
          setSelectedDormitory(null);
          await fetchDormitories();
        } catch (error) {
          message.error(error.response?.data?.message || '删除寝室失败');
        }
      },
    });
  };

  const toggleDormitorySelection = id => {
    setSelectedDormitoryIds(ids => ids.includes(id) ? ids.filter(item => item !== id) : [...ids, id]);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedDormitoryIds([]);
    setBatchGenderType(undefined);
  };

  const updateSelectedDormitories = async () => {
    if (!batchGenderType || !selectedDormitoryIds.length) return;
    try {
      await Promise.all(selectedDormitoryIds.map(id => api.put(`/accommodation/dormitories/${id}/gender`, { genderType: batchGenderType })));
      message.success(`已更新 ${selectedDormitoryIds.length} 间寝室类型`);
      exitSelectionMode();
      await fetchDormitories();
    } catch (error) {
      message.error(error.response?.data?.message || '批量更新寝室类型失败');
    }
  };

  const deleteSelectedDormitories = () => {
    if (!selectedDormitoryIds.length) return;
    Modal.confirm({
      title: '批量删除寝室',
      content: `确认删除选中的 ${selectedDormitoryIds.length} 间寝室吗？有入住学员的寝室将不会被删除。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        const results = await Promise.allSettled(selectedDormitoryIds.map(id => api.delete(`/accommodation/dormitories/${id}`)));
        const deleted = results.filter(result => result.status === 'fulfilled').length;
        const failed = results.length - deleted;
        message.success(`已删除 ${deleted} 间寝室${failed ? `，${failed} 间未删除` : ''}`);
        exitSelectionMode();
        await fetchDormitories();
      },
    });
  };

  const importDormitories = async file => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/accommodation/dormitories/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { successCount, totalCount, errors } = response.data;
      if (errors?.length) {
        Modal.warning({
          title: `已导入 ${successCount}/${totalCount} 间寝室`,
          content: <div className="import-error-list">{errors.map((error, index) => <div key={index}>{error}</div>)}</div>,
        });
      } else {
        message.success(`成功导入 ${successCount} 间寝室`);
      }
      setPage(1);
      await fetchDormitories();
    } catch (error) {
      message.error(error.response?.data?.message || '导入寝室失败');
    }
    return false;
  };

  const exportDormitories = () => {
    const rows = [];
    filteredDormitories.forEach(dormitory => {
      const residentsByBed = new Map(dormitory.residents.map(student => [String(student.bedNumber || '').replace(/\D/g, ''), student]));
      for (let bed = 1; bed <= dormitory.bedCapacity; bed += 1) {
        const student = residentsByBed.get(String(bed));
        rows.push({
          '寝室楼': String(dormitory.building || '').replace(/栋$/, ''),
          '寝室': dormitory.dormitoryNumber || '',
          '床位': bed,
          '姓名': student?.name || '',
          '性别': student?.gender || '',
          '学校': student?.school || '',
          '升本专业': student?.major || '',
          '本人联系方式': student?.phone || '',
          '班级': student?.className || '',
          '房型': dormitory.bedCapacity,
        });
      }
    });
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: ['寝室楼', '寝室', '床位', '姓名', '性别', '学校', '升本专业', '本人联系方式', '班级', '房型'] });
    worksheet['!cols'] = [10, 10, 8, 14, 8, 16, 16, 18, 12, 8].map(width => ({ wch: width }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '学员床位分配');
    XLSX.writeFile(workbook, '寝室床位分配表.xlsx');
  };

  const residentColumns = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '账号', dataIndex: 'account', key: 'account' },
    { title: '床位号', dataIndex: 'bedNumber', key: 'bedNumber', render: value => value || '-' },
    {
      title: '操作',
      key: 'action',
      width: 72,
      render: (_, record) => <Button type="text" icon={<EditOutlined />} aria-label={`编辑 ${record.name}`} onClick={() => openEdit(record)} />,
    },
  ];

  return (
    <div className="dormitory-management-page">
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '寝室管理' },
        ]} />
        <h2 className="page-title">寝室管理</h2>
      </div>

      <div className="dormitory-toolbar">
        <Space style={{ marginBottom: 20 }} wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索楼栋、寝室号或学员"
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder="筛选楼栋"
            value={buildingFilter}
            onChange={setBuildingFilter}
            style={{ width: 150 }}
            options={buildings.map(value => ({ label: value, value }))}
          />
          <Select allowClear placeholder="筛选寝室类型" value={genderFilter} onChange={setGenderFilter} style={{ width: 150 }} options={['男生寝室', '女生寝室', '未设置'].map(value => ({ label: value, value }))} />
          {selectionMode ? <>
            <Select placeholder="批量设置类型" value={batchGenderType} onChange={setBatchGenderType} style={{ width: 150 }} options={['男生寝室', '女生寝室', '未设置'].map(value => ({ label: value, value }))} />
            <Button type="primary" disabled={!batchGenderType || !selectedDormitoryIds.length} onClick={updateSelectedDormitories}>变更类型（{selectedDormitoryIds.length}）</Button>
            <Button danger disabled={!selectedDormitoryIds.length} onClick={deleteSelectedDormitories}>批量删除（{selectedDormitoryIds.length}）</Button>
            <Button onClick={exitSelectionMode}>取消选择</Button>
          </> : <Button onClick={() => setSelectionMode(true)}>批量选择</Button>}
          <Upload beforeUpload={importDormitories} showUploadList={false} accept=".xlsx,.xls">
            <Button icon={<UploadOutlined />}>导入寝室</Button>
          </Upload>
          <Button type="text" icon={<DownloadOutlined />} href="/template-dormitories.xlsx" download="寝室导入模板.xlsx">
            下载模板
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportDormitories}>导出寝室数据</Button>
        </Space>
      </div>

      {loading ? null : filteredDormitories.length === 0 ? (
        <Empty description="暂无寝室数据，请先导入寝室" />
      ) : (
        <div className="dormitory-building-list">
          <div className="dormitory-bed-legend" aria-label="床位状态图例">
            <span><i className="dormitory-bed-legend-light dormitory-bed-legend-light-occupied" />已入住</span>
            <span><i className="dormitory-bed-legend-light dormitory-bed-legend-light-vacant" />空床位</span>
          </div>
          <div className="dormitory-grid">
            {pagedDormitories.map(dormitory => {
              const bedSlots = getDormitoryBedSlots(dormitory);

              return (
                <button
                  className={`dormitory-tile ${dormitory.locked ? 'dormitory-tile-locked' : ''} ${selectedDormitoryIds.includes(dormitory.id) ? 'dormitory-tile-selected' : ''}`}
                  type="button"
                  key={dormitory.id}
                  onClick={() => selectionMode
                    ? toggleDormitorySelection(dormitory.id)
                    : (setSelectedDormitory(dormitory), setEditingGenderType(dormitory.genderType || '未设置'))}
                  aria-label={`查看 ${dormitory.building}-${dormitory.dormitoryNumber} 寝室`}
                >
                  <span className="dormitory-tile-number">{dormitory.building}-{dormitory.dormitoryNumber}</span>
                  {dormitory.locked ? <span className="dormitory-tile-lock"><LockOutlined /> 已锁定</span> : <span className="dormitory-tile-type">{dormitory.genderType || '未设置'}</span>}
                  <span className="dormitory-tile-count">{dormitory.residents.length} / {dormitory.bedCapacity} 床</span>
                  {dormitory.locked && (
                    <span className="dormitory-tile-lock-reason">锁定原因：{dormitory.lockReason || '未填写'}</span>
                  )}
                  <span className="dormitory-bed-status-grid" role="list" aria-label={`${dormitory.dormitoryNumber}床位状态`}>
                    {bedSlots.map(({ bedNumber, resident }) => {
                      const occupied = Boolean(resident);
                      const statusText = occupied ? `${resident.name || '学员'}，已入住` : '空床位';

                      return (
                        <span
                          className={`dormitory-bed-status ${occupied ? 'dormitory-bed-status-occupied' : 'dormitory-bed-status-vacant'}`}
                          key={bedNumber}
                          role="listitem"
                          title={`${bedNumber}号床：${statusText}`}
                          aria-label={`${bedNumber}号床，${statusText}`}
                        >
                          <BedIcon className="dormitory-bed-icon" aria-hidden="true" focusable="false" />
                          <span className="dormitory-bed-number">{bedNumber}</span>
                        </span>
                      );
                    })}
                  </span>
                </button>
              );
            })}
          </div>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={filteredDormitories.length}
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }}
            showSizeChanger
            pageSizeOptions={[30, 60, 90]}
            showTotal={total => `共 ${total} 间寝室`}
          />
        </div>
      )}

      <Modal
        title={`${selectedDormitory?.building || ''} ${selectedDormitory?.dormitoryNumber || ''}`.trim()}
        open={Boolean(selectedDormitory)}
        onCancel={() => setSelectedDormitory(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedDormitory(null)}>关闭</Button>,
          selectedDormitory?.locked
            ? <Button key="unlock" icon={<UnlockOutlined />} onClick={unlockDormitory}>解锁寝室</Button>
            : <Button key="lock" danger icon={<LockOutlined />} onClick={openLockModal}>锁定寝室</Button>,
          <Button key="delete" danger onClick={deleteDormitory}>删除寝室</Button>,
          <Button key="save" type="primary" onClick={saveDormitoryGender}>保存寝室类型</Button>,
        ]}
        width={720}
      >
        {selectedDormitory && <>
          <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="床位数">{selectedDormitory.bedCapacity}</Descriptions.Item>
            <Descriptions.Item label="寝室类型">
              <Select value={editingGenderType} onChange={setEditingGenderType} style={{ width: 130 }} options={['男生寝室', '女生寝室', '未设置'].map(value => ({ label: value, value }))} />
            </Descriptions.Item>
            <Descriptions.Item label="入住人数">{selectedDormitory.residents.length}</Descriptions.Item>
            <Descriptions.Item label="锁定状态">{selectedDormitory.locked ? '已锁定' : '可用'}</Descriptions.Item>
            {selectedDormitory.locked && <Descriptions.Item label="锁定原因" span={2}>{selectedDormitory.lockReason || '-'}</Descriptions.Item>}
          </Descriptions>
          <Table columns={residentColumns} dataSource={selectedDormitory.residents} rowKey="id" pagination={false} />
        </>}
      </Modal>

      <Modal
        title="锁定寝室"
        open={lockOpen}
        onCancel={() => setLockOpen(false)}
        onOk={lockDormitory}
        okText="确认锁定"
        cancelText="取消"
        confirmLoading={locking}
      >
        <Form layout="vertical">
          <Form.Item label="锁定原因" required>
            <Input.TextArea value={lockReason} onChange={event => setLockReason(event.target.value)} placeholder="例如：维修中、预留房间、暂不开放" maxLength={500} showCount rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑住宿信息 - ${editingStudent?.name || ''}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveAccommodation}
        okText="保存"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="building" label="楼栋">
            <AutoComplete options={buildings.map(value => ({ value }))} filterOption>
              <Input placeholder="选择或输入楼栋" />
            </AutoComplete>
          </Form.Item>
          <Form.Item name="dormitoryNumber" label="寝室号">
            <AutoComplete options={dormitoryNumbers.map(value => ({ value }))} filterOption>
              <Input placeholder="选择或输入寝室号" />
            </AutoComplete>
          </Form.Item>
          <Form.Item name="bedNumber" label="床位号">
            <AutoComplete options={bedNumbers.map(value => ({ value }))} filterOption>
              <Input placeholder="选择或输入床位号" />
            </AutoComplete>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DormitoryManagementPage;
